import { Box, Paper, Typography, useMediaQuery } from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import DirectionsBike from "@material-ui/icons/DirectionsBike"
import LocalOffer from "@material-ui/icons/LocalOffer"
import EmojiEvents from "@material-ui/icons/EmojiEvents"
import Notifications from "@material-ui/icons/Notifications"
import CreditCard from "@material-ui/icons/CreditCard"
import Stars from "@material-ui/icons/Stars"
import LocalDining from "@material-ui/icons/LocalDining"
import TableBar from "@material-ui/icons/TableChart"
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects"
import React, { useEffect } from "react"
import { useHistory } from "react-router-dom"
import { useSnackbar } from "../../../utils/notificacoes"
import { useEstrategiasFornecedorContext } from "../context/useEstrategiasFornecedorContext"
import type { EstrategiaItemModel } from "../models/estrategiasFornecedorModel"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "1100px",
    },
    headerRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
    },
    subtitleText: {
        color: "#7f8c8d",
        marginTop: theme.spacing(0.5),
    },
    scoreCard: {
        borderRadius: 15,
        padding: theme.spacing(2),
        textAlign: "center",
        backgroundColor: "white",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        minWidth: 160,
    },
    scoreValue: {
        fontWeight: 700,
        fontSize: "1.8rem",
        color: "#4caf50",
    },
    scoreLabel: {
        color: "#7f8c8d",
        fontSize: "0.85rem",
    },
    sectionTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(0.5),
        marginTop: theme.spacing(3),
    },
    sectionSubtitle: {
        color: "#7f8c8d",
        marginBottom: theme.spacing(2),
        fontSize: "0.9rem",
    },
    divider: {
        borderTop: "2px solid #e9ecef",
        margin: theme.spacing(3, 0),
    },
    cardsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: theme.spacing(2),
        [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2, 1fr)" },
        [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    card: {
        borderRadius: 15,
        padding: theme.spacing(2),
        cursor: "pointer",
        position: "relative",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
        },
    },
    cardActive: {
        backgroundColor: "#1cc88a",
        borderLeft: "4px solid #128259",
    },
    cardInactive: {
        backgroundColor: "var(--cor-erro)",
        borderLeft: "4px solid var(--cor-erro)",
    },
    cardManual: {
        backgroundColor: "#ffd56b",
        borderLeft: "4px solid #f0a500",
        cursor: "pointer",
    },
    cardPoints: {
        position: "absolute",
        top: 8,
        right: 8,
        fontSize: "0.7rem",
        color: "rgba(255,255,255,0.85)",
        fontWeight: 600,
    },
    cardDescription: {
        color: "rgba(255,255,255,0.9)",
        fontSize: "0.75rem",
        textTransform: "uppercase",
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
    },
    cardTitle: {
        color: "white",
        fontWeight: 700,
        fontSize: "0.9rem",
    },
    cardTitleManual: {
        color: "#2c3e50",
        fontWeight: 700,
        fontSize: "0.9rem",
    },
    cardIcon: {
        position: "absolute",
        bottom: 12,
        right: 12,
        color: "rgba(255,255,255,0.5)",
        fontSize: "2rem",
    },
    cardIconManual: {
        position: "absolute",
        bottom: 12,
        right: 12,
        color: "rgba(0,0,0,0.2)",
        fontSize: "2rem",
    },
}))

interface StrategiaCardProps {
    item: EstrategiaItemModel
    title: string
    pontos: number
    icon: React.ReactNode
    onClick: () => void
}

function EstrategiaCard({ item, title, pontos, icon, onClick }: StrategiaCardProps) {
    const classes = useStyles()
    return (
        <Paper
            className={`${classes.card} ${item.status ? classes.cardActive : classes.cardInactive}`}
            onClick={onClick}
        >
            <span className={classes.cardPoints}>{pontos}pts</span>
            <Typography className={classes.cardDescription}>{item.descricao}</Typography>
            <Typography className={classes.cardTitle}>{title}</Typography>
            <span className={classes.cardIcon}>{icon}</span>
        </Paper>
    )
}

export function EstrategiasFornecedorComponent() {
    const classes = useStyles()
    const theme = useTheme()
    const isSm = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, erroCarregamento } = useEstrategiasFornecedorContext()
    const history = useHistory()
    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        if (erroCarregamento) {
            enqueueSnackbar(erroCarregamento, { variant: "error" })
        }
    }, [erroCarregamento])

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const irPara = (rota: string) => history.push(rota)

    const naoMigrado = () =>
        alertStart("Configure esta funcionalidade no sistema legado.", "info")

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box className={classes.headerRow}>
                    <Box>
                        <Typography variant="h5" className={classes.titleText}>
                            Estratégias Automáticas
                        </Typography>
                        <Typography className={classes.subtitleText}>
                            Aqui você tem acesso a algumas das estratégias de aumentar suas vendas no aplicativo.
                        </Typography>
                    </Box>
                    <Paper className={classes.scoreCard}>
                        <EmojiObjectsIcon style={{ color: "#4caf50", fontSize: 28 }} />
                        <Typography className={classes.scoreValue}>{dados.pontuacao}/60</Typography>
                        <Typography className={classes.scoreLabel}>Pontuação</Typography>
                    </Paper>
                </Box>

                <Box className={classes.cardsGrid}>
                    <EstrategiaCard
                        item={dados.freteGratis}
                        title="FRETE GRÁTIS"
                        pontos={15}
                        icon={<DirectionsBike style={{ fontSize: "2rem" }} />}
                        onClick={() => irPara("/servicos/ferramenta/definir-area-de-entrega")}
                    />
                    <EstrategiaCard
                        item={dados.cupomAutomatico}
                        title="CUPOM DE INCENTIVO"
                        pontos={15}
                        icon={<LocalOffer style={{ fontSize: "2rem" }} />}
                        onClick={naoMigrado}
                    />
                    <EstrategiaCard
                        item={dados.selosPremiados}
                        title="SELOS PREMIADOS"
                        pontos={5}
                        icon={<Stars style={{ fontSize: "2rem" }} />}
                        onClick={naoMigrado}
                    />
                    <EstrategiaCard
                        item={dados.notificacoes}
                        title="NOTIFICAÇÕES"
                        pontos={5}
                        icon={<Notifications style={{ fontSize: "2rem" }} />}
                        onClick={() => irPara("/clientes")}
                    />
                    <EstrategiaCard
                        item={dados.fidelidade}
                        title="FIDELIDADE"
                        pontos={10}
                        icon={<EmojiEvents style={{ fontSize: "2rem" }} />}
                        onClick={naoMigrado}
                    />
                    <EstrategiaCard
                        item={dados.promocaoDoDia}
                        title="PROMOÇÃO DO DIA"
                        pontos={5}
                        icon={<LocalDining style={{ fontSize: "2rem" }} />}
                        onClick={naoMigrado}
                    />
                    <EstrategiaCard
                        item={dados.formaDePagamento}
                        title="FORMA DE PAGAMENTO"
                        pontos={5}
                        icon={<CreditCard style={{ fontSize: "2rem" }} />}
                        onClick={() => irPara("/configuracao-formas-pagamento")}
                    />
                </Box>

                <hr className={classes.divider} />

                <Typography variant="h5" className={classes.sectionTitle}>
                    Estratégias Manuais
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                    Acesse as ferramentas para implementar estratégias no seu estabelecimento.
                </Typography>

                <Box className={classes.cardsGrid}>
                    <Paper
                        className={`${classes.card} ${classes.cardManual}`}
                        onClick={() =>
                            dados.pedidosMesa
                                ? irPara("/qr-codes-mesas")
                                : alertStart(
                                      "Este estabelecimento não possui o módulo Pedidos de Mesa ativo. Consulte o nosso Suporte.",
                                      "info"
                                  )
                        }
                    >
                        <Typography className={classes.cardTitleManual}>PEDIDO MESA</Typography>
                        <span className={classes.cardIconManual}>
                            <TableBar style={{ fontSize: "2rem" }} />
                        </span>
                    </Paper>
                </Box>
            </Box>
        </Box>
    )
}
